import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, CalendarCheck, CalendarClock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const WELCOME = {
  role: "assistant",
  content:
    "Hi! I'm Beaker Assistant \u2014 I can answer questions about Cloud Beaker's multi-cloud services or help you book a consultation on our calendar. How can I help?",
};

const SUGGESTIONS = [
  "What services do you offer?",
  "Which cloud platforms do you support?",
  "Book a cloud assessment",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [booked, setBooked] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setSessionId(data.session_id);
      if (data.appointment) setBooked(true);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I'm having trouble right now. Please call us at +1 (210) 201-2513 or email hello@cloudbeaker.com.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close assistant" : "Open assistant"}
        data-testid="chat-launcher"
        className="fixed bottom-6 right-6 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-panel transition-transform duration-200 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {open ? <X size={24} /> : <MessageCircle size={26} />}
      </button>

      {/* Panel */}
      <div
        data-testid="chat-panel"
        className={`fixed bottom-24 right-4 z-[70] flex w-[calc(100vw-2rem)] max-w-[384px] flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-panel transition-[opacity,transform] duration-200 sm:right-6 ${
          open ? "pointer-events-auto opacity-100 translate-y-0" : "pointer-events-none translate-y-3 opacity-0"
        }`}
        style={{ height: "min(560px, 72vh)" }}
        role="dialog"
        aria-label="Cloud Beaker assistant"
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border bg-gradient-to-r from-primary to-[#1f6fe0] px-4 py-3.5 text-white">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
            <MessageCircle size={18} />
          </span>
          <div className="leading-tight">
            <p className="text-sm font-bold">Beaker Assistant</p>
            <p className="text-xs text-white/80">Ask about our services or book a call</p>
          </div>
        </div>

        {/* Book a consultation shortcut */}
        <Link
          to="/book"
          onClick={() => setOpen(false)}
          data-testid="chat-book-cta"
          className="flex items-center justify-center gap-2 border-b border-border bg-cbblue-soft/50 px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-cbblue-soft"
        >
          <CalendarClock size={15} /> Book a consultation on our calendar
        </Link>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-cbmist/40 px-4 py-4" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed ${
                  m.role === "user"
                    ? "rounded-br-sm bg-primary text-white"
                    : "rounded-bl-sm border border-border bg-white text-cbink"
                }`}
              >
                {m.role === "assistant" ? m.content.replace(/\*\*/g, "") : m.content}
              </div>
            </div>
          ))}

          {booked && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-cbgreen/30 bg-cbgreen-soft px-3 py-2.5 text-[13px] font-semibold text-cbgreen" data-testid="chat-booked-badge">
              <CalendarCheck size={16} /> Ready to lock in a time?
              <Link to="/book" onClick={() => setOpen(false)} className="ml-auto inline-flex items-center gap-1 rounded-lg bg-cbgreen px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90">
                <CalendarClock size={13} /> Pick a slot
              </Link>
            </div>
          )}

          {loading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm border border-border bg-white px-3.5 py-3">
                <span className="h-2 w-2 animate-bounce rounded-full bg-cbslate/60 [animation-delay:-0.2s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-cbslate/60 [animation-delay:-0.1s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-cbslate/60" />
              </div>
            </div>
          )}

          {messages.length <= 1 && !loading && (
            <div className="flex flex-wrap gap-2 pt-1">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid="chat-suggestion"
                  className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-medium text-cbink transition-colors hover:border-primary hover:text-primary"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        <div className="flex items-center gap-2 border-t border-border bg-white p-3">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type your message..."
            data-testid="chat-input"
            className="flex-1 rounded-xl border border-border bg-cbmist/50 px-3.5 py-2.5 text-[14px] text-cbink outline-none transition-colors focus:border-primary"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={loading || !input.trim()}
            aria-label="Send message"
            data-testid="chat-send"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </>
  );
}
